import React, { useState } from 'react';
import {
  Table, TableRow, TableCell, TableHead, TableBody,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import makeStyles from '@mui/styles/makeStyles';
import { useCatch, useEffectAsync } from '../reactHelper';
import { formatBoolean, formatTime } from '../common/util/formatter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import { useAdministrator } from '../common/util/permissions';
import SearchHeader, { filterByKeyword } from './components/SearchHeader';
import { usePreference } from '../common/util/preferences';

const useStyles = makeStyles((theme) => ({
  columnAction: {
    width: '1%',
    border: '1px solid #ced7df',
    padding: theme.spacing(1),
    margin: '20px',
  },
  headerTable: {
    '&.MuiTable-root': {
      width: 'auto',
      margin: '15px',
    },
    '&.MuiTableHead-root': {
      backgroundColor: '#ced7df',
      border: '1px solid #ced7df',
    },
    '&.MuiTableCell-root': {
      border: '1px solid #ced7df',
    },
  },
  colorCell: {
    '&.MuiTable-root': {
      width: 'auto',
      margin: '15px',
    },
    '&.MuiTableHead-root': {
      backgroundColor: '#ced7df',
      border: '1px solid #ced7df',
    },
    '&.MuiTableCell-root': {
      border: '1px solid #ced7df',
    },
    '&.MuiTableRow-root:hover': {
      backgroundColor: '#f5faff',
    },
  },
}));

const UsersPage = () => {
  const classes = useStyles();
  const t = useTranslation();

  const admin = useAdministrator();

  const hours12 = usePreference('twelveHourFormat');

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = useCatch(async (userId) => {
    const response = await fetch(`/api/session/${userId}`);
    if (response.ok) {
      window.location.replace('/');
    } else {
      throw Error(await response.text());
    }
  });

  const loginAction = {
    title: t('loginLogin'),
    icon: (<LoginIcon fontSize="small" />),
    handler: handleLogin,
  };

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        setItems(await response.json());
      } else {
        throw Error(await response.text());
      }
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'settingsUsers']}>
      <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />
      <Table className={classes.headerTable}>
        <TableHead className={classes.headerTable}>
          <TableRow className={classes.headerTable}>
            <TableCell className={classes.headerTable}>ID</TableCell>
            <TableCell className={classes.headerTable}>{t('sharedName')}</TableCell>
            <TableCell className={classes.headerTable}>{t('userEmail')}</TableCell>
            <TableCell className={classes.headerTable}>{t('userAdmin')}</TableCell>
            <TableCell className={classes.headerTable}>{t('sharedDisabled')}</TableCell>
            <TableCell className={classes.headerTable}>{t('userExpirationTime')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? items.filter(filterByKeyword(searchKeyword)).map((item) => (
            <TableRow className={classes.colorCell} key={item.id}>
              <TableCell className={classes.headerTable}>{item.id}</TableCell>
              <TableCell className={classes.headerTable}>{item.name}</TableCell>
              <TableCell className={classes.headerTable}>{item.email}</TableCell>
              <TableCell className={classes.headerTable}>{formatBoolean(item.administrator, t)}</TableCell>
              <TableCell className={classes.headerTable}>{formatBoolean(item.disabled, t)}</TableCell>
              <TableCell className={classes.headerTable}>{formatTime(item.expirationTime, 'date', hours12)}</TableCell>
              <TableCell className={classes.columnAction} padding="none">
                <CollectionActions
                  itemId={item.id}
                  editPath="/settings/user"
                  endpoint="users"
                  setTimestamp={setTimestamp}
                  customAction={admin ? loginAction : null}
                />
              </TableCell>
            </TableRow>
          )) : (<TableShimmer columns={6} endAction />)}
        </TableBody>
      </Table>
      <CollectionFab editPath="/settings/user" />
    </PageLayout>
  );
};

export default UsersPage;
